import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getActiveRole } from '../../utils/auth';
import { resolveNotificationDestination } from '../../utils/notificationDisplay';
import {
    getUnreadCount,
    listNotificationsPage,
    markAllRead,
    markRead,
} from '../../api/notifications';
import ListPageLayout, { ListPageHeader, ListLoadingSpinner } from '../../components/layout/ListPageLayout';
import Button from '../../components/common/Button';
import NotificationItem from '../../components/NotificationItem';

export default function NotificationsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [loadError, setLoadError] = useState(false);
    const [actionError, setActionError] = useState('');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [unread, setUnread] = useState(0);
    const size = 30;

    const relativeTime = (iso) => {
        if (!iso) return '';
        const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (diff < 60) return t('notificationsPage.justNow');
        if (diff < 3600) return t('notificationsPage.minutesAgo', { count: Math.floor(diff / 60) });
        if (diff < 86400) return t('notificationsPage.hoursAgo', { count: Math.floor(diff / 3600) });
        return t('notificationsPage.daysAgo', { count: Math.floor(diff / 86400) });
    };

    const load = useCallback(async (pageNum = 0) => {
        setLoading(true);
        setLoadError(false);
        setActionError('');
        try {
            const [data, count] = await Promise.all([
                listNotificationsPage({ page: pageNum, size }),
                getUnreadCount(),
            ]);
            setItems(data.content || []);
            setPage(data.page ?? pageNum);
            setTotalPages(data.totalPages ?? 0);
            setTotalElements(data.totalElements ?? 0);
            setUnread(count);
        } catch {
            setItems([]);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(0);
    }, [load]);

    const handleOpen = async (item) => {
        if (!item.read) {
            try {
                await markRead(item.id);
                setUnread((prev) => Math.max(0, prev - 1));
                setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, read: true } : n)));
            } catch {
                /* stay unread — do not pretend success */
            }
        }
        const resolved = resolveNotificationDestination(item, getActiveRole());
        if (resolved) navigate(resolved);
    };

    const handleMarkAllRead = async () => {
        try {
            await markAllRead();
            setUnread(0);
            setItems((prev) => prev.map((n) => ({ ...n, read: true })));
            setActionError('');
        } catch {
            setActionError(t('notificationsPage.markAllFailed', {
                defaultValue: 'Could not mark all as read. Try again.',
            }));
        }
    };

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t('notificationsPage.title')}
                subtitle={t('notificationsPage.subtitleValue')}
                actions={unread > 0 ? (
                    <Button type="button" variant="secondary" onClick={handleMarkAllRead}>
                        {t('notificationsPage.markAllRead')}
                    </Button>
                ) : null}
            />

            {!loading && unread > 0 && (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    {t('notificationsPage.unreadBanner', { count: unread })}
                </div>
            )}
            {loadError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {t('common.listLoadFailed')}
                </div>
            )}
            {actionError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {actionError}
                </div>
            )}

            {loading ? (
                <ListLoadingSpinner />
            ) : loadError ? null : items.length === 0 ? (
                <div className="list-empty-state">
                    <p className="font-medium text-gray-700">{t('notificationsPage.empty')}</p>
                    <p className="mt-1 text-sm text-gray-500">{t('notificationsPage.emptyHint')}</p>
                </div>
            ) : (
                <div className="list-section-card !overflow-hidden !p-0 divide-y divide-slate-100">
                    {items.map((item) => (
                        <NotificationItem
                            key={item.id}
                            item={item}
                            relativeTime={relativeTime(item.createdAt)}
                            onOpen={handleOpen}
                        />
                    ))}
                </div>
            )}

            {!loading && totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                        {t('notificationsPage.pageOf', { page: page + 1, totalPages, total: totalElements })}
                    </p>
                    <div className="flex gap-2">
                        <Button type="button" variant="secondary" disabled={page <= 0} onClick={() => load(page - 1)}>
                            {t('notificationsPage.previous')}
                        </Button>
                        <Button type="button" variant="secondary" disabled={page >= totalPages - 1} onClick={() => load(page + 1)}>
                            {t('notificationsPage.next')}
                        </Button>
                    </div>
                </div>
            )}
        </ListPageLayout>
    );
}
